import React, { useCallback, useRef, useState } from "react";
import { UploadCloud, FileSpreadsheet, Download, X, CheckCircle2, AlertTriangle } from "lucide-react";
import { uploadFile, templateUrl } from "../api";

export default function UploadModal({ open, onClose, onUploaded }) {
  const [dragging, setDragging] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const inputRef = useRef(null);

  const handleFiles = useCallback(async (files) => {
    if (!files || !files.length) return;
    const file = files[0];
    setBusy(true);
    setError("");
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const data = await uploadFile(fd);
      setResult(data);
      onUploaded && onUploaded(data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Upload failed. Check your file format.");
    } finally {
      setBusy(false);
    }
  }, [onUploaded]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="upload-modal">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative z-10 w-full max-w-xl rounded-2xl border border-slate-700 bg-[#161f32] p-6 shadow-2xl fade-up">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="font-display text-xl font-semibold text-slate-100">Import Customer List</h2>
          <button data-testid="upload-modal-close" onClick={onClose} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-800 hover:text-slate-200">
            <X className="h-5 w-5" />
          </button>
        </div>

        {!result && (
          <>
            <div
              data-testid="upload-dropzone"
              onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
              onDragLeave={() => setDragging(false)}
              onDrop={(e) => { e.preventDefault(); setDragging(false); handleFiles(e.dataTransfer.files); }}
              onClick={() => inputRef.current?.click()}
              className={`flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed p-10 text-center transition-colors ${
                dragging ? "border-blue-500 bg-blue-500/5" : "border-slate-600 hover:border-slate-500"
              }`}
            >
              <UploadCloud className={`mb-3 h-10 w-10 ${dragging ? "text-blue-400" : "text-slate-500"}`} />
              <p className="text-sm font-medium text-slate-200">
                {busy ? "Uploading & parsing..." : "Drag & drop your CSV / Excel file"}
              </p>
              <p className="mt-1 text-xs text-slate-500">or click to browse — up to 9 serial numbers per customer</p>
              <input
                ref={inputRef}
                data-testid="upload-file-input"
                type="file"
                accept=".csv,.xlsx,.xls,.txt"
                className="hidden"
                onChange={(e) => handleFiles(e.target.files)}
              />
            </div>

            {error && (
              <p data-testid="upload-error" className="mt-3 rounded-lg border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-sm text-rose-300">
                {error}
              </p>
            )}

            <div className="mt-4 flex items-center justify-between rounded-lg border border-slate-700 bg-slate-900/50 px-4 py-3">
              <div className="flex items-center gap-2 text-sm text-slate-300">
                <FileSpreadsheet className="h-4 w-4 text-emerald-400" />
                Need the format? Download the template.
              </div>
              <a
                data-testid="download-template-link"
                href={templateUrl}
                className="inline-flex items-center gap-1.5 rounded-lg bg-slate-800 px-3 py-1.5 text-xs font-semibold text-slate-200 hover:bg-slate-700"
              >
                <Download className="h-3.5 w-3.5" /> Template
              </a>
            </div>
          </>
        )}

        {result && (
          <div data-testid="upload-result" className="fade-up">
            <div className="flex flex-col items-center py-4 text-center">
              {result.created === 0 && result.duplicate_count > 0 ? (
                <AlertTriangle className="mb-2 h-12 w-12 text-amber-400" />
              ) : (
                <CheckCircle2 className="mb-2 h-12 w-12 text-emerald-400" />
              )}
              <p className="text-lg font-semibold text-slate-100">{result.created} customer(s) imported</p>
              {result.skipped > 0 && <p className="text-sm text-slate-400">{result.skipped} empty row(s) skipped</p>}
              <p className="mt-2 text-xs text-slate-500">
                Detected {result.serial_columns?.length || 0} serial column(s):{" "}
                {(result.serial_columns || []).join(", ") || "none"}
              </p>
            </div>
            {(result.warnings || []).map((w, i) => (
              <div key={i} data-testid="upload-warning" className="mt-3 rounded-lg border border-amber-500/50 bg-amber-500/10 px-4 py-3 text-sm text-amber-200">
                <p className="flex items-center gap-2 font-semibold"><AlertTriangle className="h-4 w-4" /> Action needed</p>
                <p className="mt-1 text-xs text-amber-200/90">{w}</p>
              </div>
            ))}
            {(result.ai_header_notes || []).length > 0 && (
              <p className="mt-2 text-xs text-sky-300">{result.ai_header_notes.join(" · ")}</p>
            )}
            {result.duplicate_count > 0 && (
              <div data-testid="upload-duplicate-warning" className="mb-2 rounded-lg border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-300">
                <p className="flex items-center gap-1.5 font-semibold">
                  <AlertTriangle className="h-4 w-4" /> {result.duplicate_count} duplicate Job #(s) skipped
                </p>
                <p className="mt-1 text-xs text-amber-400/90">
                  Already queued — not re-imported to prevent duplicate registrations:{" "}
                  <span className="font-mono">{(result.duplicates || []).slice(0, 12).join(", ")}{(result.duplicates || []).length > 12 ? "…" : ""}</span>
                </p>
              </div>
            )}
            <button
              data-testid="upload-done-button"
              onClick={onClose}
              className="mt-2 w-full rounded-xl bg-blue-600 py-2.5 font-semibold text-white hover:bg-blue-500"
            >
              Done
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
